import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  api.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.requires_2fa) {
      return { requires_2fa: true, email };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return { success: true, user: userData };
  };

  const verify2FA = async (email, code) => {
    const response = await api.post('/auth/verify-2fa', { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return { success: true, user: userData };
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  };

  const verifyEmail = async (email, code) => {
    const response = await api.post('/auth/verify-email', { email, code });
    return response.data;
  };

  const enable2FA = async () => {
    const response = await api.post('/auth/enable-2fa');
    return response.data;
  };

  // Direct login for waiter/driver with code-based auth
  const loginWithToken = (userData, accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'super_admin';
  const isRestaurantAdmin = () => user?.role === 'restaurant_admin';
  const isDriver = () => user?.role === 'driver';
  const isSalesPerson = () => user?.role === 'sales_person';
  const isCustomer = () => user?.role === 'customer';

  const value = {
    user,
    token,
    loading,
    login,
    loginWithToken,
    logout,
    register,
    verifyEmail,
    verify2FA,
    enable2FA,
    isAdmin,
    isRestaurantAdmin,
    isDriver,
    isSalesPerson,
    isCustomer,
    api,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
